import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUnpaidInvoices, markInvoiceAsPaid, toggleDepositCollected, updateInvoice } from '../services/invoices';
import { createAuditLog } from '../services/audit';
import { sendInvoiceEmail } from '../services/email';
import { getLetterTemplate, getDefaultLetterTemplates } from '../services/letterTemplates';
import { renderTemplate } from '../utils/templateRenderer';
import { assembleDocument } from '../utils/assembleDocument';
import './UnpaidInvoices.css';

export default function UnpaidInvoices() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printInvoice, setPrintInvoice] = useState(null);
  const [letterTemplate, setLetterTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    depositCollected: 'all', // 'all', 'yes', 'no'
    status: 'all', // 'all', 'sent', 'overdue'
    clientName: '',
    minAmount: '',
    maxAmount: '',
    dateFrom: '',
    dateTo: '',
  });

  const navigate = useNavigate();

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getUnpaidInvoices();
      setInvoices(data);
      setFilteredInvoices(data);
    } catch (error) {
      console.error('Error loading unpaid invoices:', error);
      alert('Failed to load unpaid invoices: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Apply filters
  useEffect(() => {
    let filtered = [...invoices];

    // Deposit collected filter
    if (filters.depositCollected === 'yes') {
      filtered = filtered.filter(inv => inv.deposit_collected === true);
    } else if (filters.depositCollected === 'no') {
      filtered = filtered.filter(inv => !inv.deposit_collected);
    }

    // Status filter
    if (filters.status === 'sent') {
      filtered = filtered.filter(inv => inv.status === 'sent');
    } else if (filters.status === 'overdue') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      filtered = filtered.filter(inv => {
        if (!inv.due_date) return false;
        const dueDate = new Date(inv.due_date);
        dueDate.setHours(0, 0, 0, 0);
        return dueDate < today;
      });
    }

    // Client name filter
    if (filters.clientName) {
      const searchTerm = filters.clientName.toLowerCase();
      filtered = filtered.filter(inv => {
        const client = inv.clients;
        return client?.name?.toLowerCase().includes(searchTerm) ||
               client?.email?.toLowerCase().includes(searchTerm);
      });
    }

    // Amount filters
    if (filters.minAmount) {
      const min = parseFloat(filters.minAmount);
      filtered = filtered.filter(inv => parseFloat(inv.total || 0) >= min);
    }
    if (filters.maxAmount) {
      const max = parseFloat(filters.maxAmount);
      filtered = filtered.filter(inv => parseFloat(inv.total || 0) <= max);
    }

    // Date filters
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(inv => {
        const invDate = new Date(inv.date);
        invDate.setHours(0, 0, 0, 0);
        return invDate >= fromDate;
      });
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(inv => {
        const invDate = new Date(inv.date);
        invDate.setHours(0, 0, 0, 0);
        return invDate <= toDate;
      });
    }

    setFilteredInvoices(filtered);
  }, [invoices, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      depositCollected: 'all',
      status: 'all',
      clientName: '',
      minAmount: '',
      maxAmount: '',
      dateFrom: '',
      dateTo: '',
    });
  };

  const handleMarkAsPaid = async (invoiceId, invoiceNumber) => {
    if (window.confirm(`Mark invoice ${invoiceNumber || invoiceId} as paid?`)) {
      try {
        const invoice = invoices.find(inv => inv.id === invoiceId);
        await markInvoiceAsPaid(invoiceId);
        await createAuditLog('mark_paid', 'invoice', invoiceId, {
          invoice_number: invoiceNumber,
          client_name: invoice?.clients?.name,
          total: invoice?.total
        });
        await loadInvoices();
        alert('Invoice marked as paid successfully!');
      } catch (error) {
        alert('Failed to mark invoice as paid: ' + error.message);
      }
    }
  };

  const handleDepositToggle = async (invoiceId, currentValue) => {
    try {
      await toggleDepositCollected(invoiceId, !currentValue);
      await loadInvoices();
    } catch (error) {
      alert('Failed to update deposit status: ' + error.message);
    }
  };

  const handleHideTotalsToggle = async (invoiceId, currentValue) => {
    try {
      const newValue = !currentValue;
      await updateInvoice(invoiceId, { hide_totals: newValue });
      await loadInvoices();
    } catch (error) {
      alert('Failed to update hide totals status: ' + error.message);
    }
  };

  const handleViewClient = (clientId, invoiceId = null) => {
    navigate(`/clients/${clientId}`, { state: { activeTab: 'history', invoiceId: invoiceId } });
  };

  const handlePrint = (invoice) => {
    setPrintInvoice(invoice);
  };

  const handleEmail = async (invoice) => {
    try {
      const client = invoice.clients;
      if (!client || !client.email) {
        alert('Client email is required to send email');
        return;
      }

      // Show info message
      alert(`A PDF invoice will be downloaded. After it downloads, your email client will open. Please attach the downloaded PDF file before sending the email.`);

      await sendInvoiceEmail(invoice, client);
    } catch (error) {
      alert('Failed to send email: ' + error.message);
    }
  };

  const parseItems = (items) => {
    try {
      return typeof items === 'string' ? JSON.parse(items) : items;
    } catch {
      return [];
    }
  };

  // Load letter template for print view
  useEffect(() => {
    const loadTemplate = async () => {
      if (!printInvoice) return;
      
      try {
        setLoadingTemplate(true);
        const template = await getLetterTemplate('invoice');
        setLetterTemplate(template);
      } catch (error) {
        console.log('Using default letter template:', error);
        const defaults = getDefaultLetterTemplates();
        setLetterTemplate(defaults.invoice);
      } finally {
        setLoadingTemplate(false);
      }
    };
    
    loadTemplate();
  }, [printInvoice]);

  // Open print dialog only after template has loaded and content has rendered
  useEffect(() => {
    if (!printInvoice || !letterTemplate || loadingTemplate) return;
    const timeoutId = setTimeout(() => window.print(), 150);
    const onAfterPrint = () => {
      setPrintInvoice(null);
      window.removeEventListener('afterprint', onAfterPrint);
    };
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, [printInvoice, letterTemplate, loadingTemplate]);

  // Print view component - uses template from Settings
  const PrintView = ({ invoice }) => {
    if (loadingTemplate || !letterTemplate) {
      return <div className="print-view">Loading template...</div>;
    }
    
    const items = parseItems(invoice.items);
    const client = invoice.clients;
    const clientCityStateZip = [client?.city, client?.state, client?.zip].filter(Boolean).join(', ') || '';
    
    // Prepare template data
    const templateData = {
      document_type: 'INVOICE',
      client: {
        name: client?.name || 'Client',
        address: client?.address || '',
        city_state_zip: clientCityStateZip,
        email: client?.email || '',
        phone: client?.phone || '',
      },
      invoice: {
        number: invoice.invoice_number || invoice.id || '',
        date: invoice.date ? new Date(invoice.date).toLocaleDateString() : '',
        due_date: invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '',
        document_type: 'INVOICE',
        subtotal: `$${parseFloat(invoice.subtotal || 0).toFixed(2)}`,
        tax: `$${parseFloat(invoice.tax_amount || 0).toFixed(2)}`,
        tax_amount: `$${parseFloat(invoice.tax_amount || 0).toFixed(2)}`,
        total: `$${parseFloat(invoice.total || 0).toFixed(2)}`,
        tax_rate: invoice.tax_rate || 0,
        taxable_subtotal: invoice.taxable_subtotal || 0,
        non_taxable_subtotal: invoice.non_taxable_subtotal || 0,
        line_items: items,
        items_table: items,
        notes: invoice.notes || '',
      },
      date: invoice.date ? new Date(invoice.date).toLocaleDateString() : '',
      due_date: invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '',
      facility: {
        name: 'Menlo Atherton Glass',
        address: 'PO Box 5214, Redwood City CA 94063',
      },
      user: {
        name: user?.user_metadata?.full_name || user?.email || '',
      },
      notes_section: invoice.notes || '',
      tax_rate: invoice.tax_rate || 0,
    };
    
    // UNIFIED PIPELINE: Assemble document then render
    // Step 1: Assemble document structure
    const assembledHTML = assembleDocument({
      header: letterTemplate.header || '',
      body: letterTemplate.body || '',
      footer: letterTemplate.footer || ''
    });
    
    // Step 2: Render with real data using unified renderTemplate
    const templateContent = renderTemplate(assembledHTML, templateData, invoice.hide_totals);
    
    return (
      <div className="print-view">
        <div 
          className="print-template-content"
          dangerouslySetInnerHTML={{ __html: templateContent }}
        />
      </div>
    );
  };

  if (printInvoice) {
    return createPortal(<PrintView invoice={printInvoice} />, document.body);
  }

  if (loading) {
    return (
      <div className="unpaid-invoices-page">
        <div className="loading-state">Loading unpaid invoices...</div>
      </div>
    );
  }

  return (
    <div className="unpaid-invoices-page">
      <div className="page-header">
        <h1>Unpaid Invoices</h1>
        <div className="header-stats">
          <span className="stat-badge">
            {filteredInvoices.length} {filteredInvoices.length === 1 ? 'Invoice' : 'Invoices'}
            {filteredInvoices.length !== invoices.length && ` (of ${invoices.length})`}
          </span>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filters-header">
          <h3>Filters</h3>
          <button className="btn-clear-filters" onClick={clearFilters}>
            Clear All
          </button>
        </div>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Deposit Collected</label>
            <select
              value={filters.depositCollected}
              onChange={(e) => handleFilterChange('depositCollected', e.target.value)}
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All</option>
              <option value="sent">Sent</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Client Name</label>
            <input
              type="text"
              placeholder="Search by client name or email"
              value={filters.clientName}
              onChange={(e) => handleFilterChange('clientName', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Min Amount ($)</label>
            <input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={filters.minAmount}
              onChange={(e) => handleFilterChange('minAmount', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Max Amount ($)</label>
            <input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={filters.maxAmount}
              onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Date From</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Date To</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
          </div>
        </div>
      </div>

      {filteredInvoices.length === 0 ? (
        <div className="empty-state">
          <p>{invoices.length === 0 ? '🎉 All invoices are paid!' : 'No invoices match the selected filters.'}</p>
          <p className="empty-subtitle">
            {invoices.length === 0 
              ? 'No unpaid invoices found.' 
              : 'Try adjusting your filters to see more results.'}
          </p>
        </div>
      ) : (
        <div className="invoices-list">
          {filteredInvoices.map((invoice) => {
            const items = parseItems(invoice.items);
            const client = invoice.clients;
            const isOverdue = invoice.due_date && new Date(invoice.due_date) < new Date() && invoice.status !== 'paid';
            
            return (
              <div key={invoice.id} className="invoice-card">
                <div className="invoice-header">
                  <div className="invoice-info">
                    <h3>
                      {invoice.invoice_number || `Invoice #${invoice.id}`}
                      {isOverdue && <span className="overdue-badge">Overdue</span>}
                      {invoice.deposit_collected && <span className="deposit-badge">Deposit Collected</span>}
                    </h3>
                    <div className="invoice-meta">
                      <span className="client-link" onClick={() => handleViewClient(invoice.client_id)}>
                        Client: {client?.name || 'Unknown Client'}
                      </span>
                      <span>Date: {new Date(invoice.date).toLocaleDateString()}</span>
                      {invoice.due_date && (
                        <span className={isOverdue ? 'overdue-date' : ''}>
                          Due: {new Date(invoice.due_date).toLocaleDateString()}
                        </span>
                      )}
                      <span className={`status-badge ${invoice.status}`}>
                        {invoice.status}
                      </span>
                    </div>
                  </div>
                  <div className="invoice-actions">
                    <span className="invoice-total">${parseFloat(invoice.total || 0).toFixed(2)}</span>
                    <div className="action-buttons">
                      <label className="deposit-checkbox-label">
                        <input
                          type="checkbox"
                          checked={invoice.deposit_collected || false}
                          onChange={() => handleDepositToggle(invoice.id, invoice.deposit_collected)}
                        />
                        <span>Deposit Collected</span>
                      </label>
                      <button
                        className="btn-action"
                        onClick={() => handlePrint(invoice)}
                        title="Print"
                      >
                        Print
                      </button>
                      <button
                        className="btn-action"
                        onClick={() => handleEmail(invoice)}
                        title="Email"
                        disabled={!invoice.clients?.email}
                      >
                        Email
                      </button>
                      <button
                        className="btn-mark-paid"
                        onClick={() => handleMarkAsPaid(invoice.id, invoice.invoice_number)}
                        title="Mark as Paid"
                      >
                        Mark as Paid
                      </button>
                      <button
                        className="btn-view-client"
                        onClick={() => handleViewClient(invoice.client_id, invoice.id)}
                        title="View Invoice"
                      >
                        View Invoice
                      </button>
                      <label className="hide-totals-checkbox-label">
                        <input
                          type="checkbox"
                          checked={invoice.hide_totals || false}
                          onChange={() => handleHideTotalsToggle(invoice.id, invoice.hide_totals)}
                        />
                        <span>Hide Totals</span>
                      </label>
                    </div>
                  </div>
                </div>

                {items.length > 0 && (
                  <div className="invoice-items">
                    <table>
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Unit Price</th>
                          <th>Taxable</th>
                          <th>Total</th>
                        </tr>
                      </thead>
            <tbody>
              {items.map((item, index) => {
                if (item.isSeparator) {
                  const prevSepIndex = items.slice(0, index).findLastIndex((x) => x.isSeparator);
                  const sectionStart = prevSepIndex >= 0 ? prevSepIndex + 1 : 0;
                  const sectionItems = items.slice(sectionStart, index).filter((i) => !i.isSeparator);
                  const sectionSum = sectionItems.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
                  const sectionTaxableSum = sectionItems
                    .filter((i) => i.taxable !== false)
                    .reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
                  const includeTax = invoice.include_tax !== false;
                  const taxRate = parseFloat(invoice.tax_rate || 0);
                  const hasSectionTax = includeTax && taxRate > 0 && sectionTaxableSum > 0;
                  const sectionTax = hasSectionTax ? sectionTaxableSum * taxRate / 100 : 0;
                  const sectionTotalWithTax = sectionSum + sectionTax;
                  return (
                    <React.Fragment key={index}>
                      {item.showSubtotal && sectionSum > 0 && (
                        <>
                          {hasSectionTax && (
                            <>
                              <tr className="item-section-subtotal-row">
                                <td colSpan={4} className="section-subtotal-label">Section Subtotal</td>
                                <td className="total-cell section-subtotal-value">${sectionSum.toFixed(2)}</td>
                              </tr>
                              <tr className="item-section-tax-row">
                                <td colSpan={4} className="section-subtotal-label">Section Tax ({taxRate}%)</td>
                                <td className="total-cell section-subtotal-value">${sectionTax.toFixed(2)}</td>
                              </tr>
                            </>
                          )}
                          <tr className="item-section-subtotal-row">
                            <td colSpan={4} className="section-subtotal-label">Section Total</td>
                            <td className="total-cell section-subtotal-value">${sectionTotalWithTax.toFixed(2)}</td>
                          </tr>
                        </>
                      )}
                      <tr className="item-separator-row">
                        <td colSpan="5" className="item-separator">
                          <div className="separator-line"></div>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                }
                return (
                  <tr key={index}>
                    <td>{item.description}</td>
                    <td>{item.quantity}</td>
                    <td>${parseFloat(item.unit_price || 0).toFixed(2)}</td>
                    <td>{item.taxable !== false ? 'Yes' : 'No'}</td>
                    <td>${parseFloat(item.total || 0).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
                    </table>
                  </div>
                )}

                {!invoice.hide_totals && (
                  <div className="invoice-summary">
                    <div className="summary-row">
                      <span>Subtotal:</span>
                      <span>${parseFloat(invoice.subtotal || 0).toFixed(2)}</span>
                    </div>
                    {parseFloat(invoice.taxable_subtotal || 0) > 0 && (
                      <div className="summary-row">
                        <span>Taxable Amount:</span>
                        <span>${parseFloat(invoice.taxable_subtotal || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {parseFloat(invoice.non_taxable_subtotal || 0) > 0 && (
                      <div className="summary-row">
                        <span>Non-Taxable Amount:</span>
                        <span>${parseFloat(invoice.non_taxable_subtotal || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {parseFloat(invoice.tax_rate || 0) > 0 && (
                      <div className="summary-row">
                        <span>Tax ({invoice.tax_rate}%):</span>
                        <span>${parseFloat(invoice.tax_amount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="summary-row total">
                      <span>Total:</span>
                      <span>${parseFloat(invoice.total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {invoice.notes && (
                  <div className="invoice-notes">
                    <strong>Notes:</strong> {invoice.notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
